import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { ReactComponent as BarChartIcon } from '../../../../assets/icons/chart-icon.svg';
import { ReactComponent as PieChartIcon } from '../../../../assets/icons/pie-chart-icon.svg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import ToggleWithIcons from '../../../../shared-components/toggle-with-icons/ToggleWithIcons';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import stationManagementApi from '../../../../utils/api/station-management';

interface ChartDataProperties {
  label: string;
  maintenanceAlert: number;
  transactionAlert: number;
  energieConsumptionAlert: number;
  performanceAlert: number;
  [key: string]: string | number;
}

/**
 * Alerts chart component
 * @returns
 */
function AlertsChart() {
  const { t, i18n } = useTranslation('stationManagement');
  const isRTL = i18n.dir() === 'rtl';
  const dispatch = useAppDispatch();
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [dataToggle, setDataToggle] = useState([
    {
      svgIcon: <PieChartIcon />,
      key: 'chart',
      isActive: true,
    },
    {
      svgIcon: <BarChartIcon />,
      key: 'calender',
      isActive: false,
    },
  ]);

  const barChartContainerReference = useRef<HTMLDivElement>(null);

  const chartTimePeriod: {
    label: string;
    value: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months';
  }[] = [
    {
      label: t('hours24'),
      value: 'last24hours',
    },
    {
      label: t('days7'),
      value: 'last7days',
    },
    {
      label: t('lastMonth'),
      value: 'lastMonth',
    },
    {
      label: t('months12'),
      value: 'last12months',
    },
  ];

  /**
   * Fetches the power consumed report data for the specified time range and updates the chart data.
   * @param range - The time period for which to fetch the report ('last24hours', 'last7days', 'lastMonth', or 'last12months').
   */
  const fetchToggleData = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    if (!selectedChargingPoint) return;
    try {
      const response = await stationManagementApi.fetchAlertsChart({
        timePeriod: range,
        reportType: 'performance',
        chargingStation: selectedChargingPoint?.id,
      });

      if (response.status === 200) {
        let transformedData: ChartDataProperties[];
        const ok = response.data;
        switch (range) {
          case 'last12months':
            transformedData = fillMissingMonthsGeneric(ok).map((item) => ({
              label: item.label,
              maintenanceAlert: (item as any).maintenanceAlert || 0,
              transactionAlert: (item as any).transactionAlert || 0,
              energieConsumptionAlert: (item as any).energieConsumptionAlert || 0,
              performanceAlert: (item as any).performanceAlert || 0,
            }));
            break;
          case 'lastMonth':
            transformedData = fillMissingLastNDaysGeneric(response.data, 30).map((item) => ({
              label: item.label,
              maintenanceAlert: (item as any).maintenanceAlert || 0,
              transactionAlert: (item as any).transactionAlert || 0,
              energieConsumptionAlert: (item as any).energieConsumptionAlert || 0,
              performanceAlert: (item as any).performanceAlert || 0,
            }));
            break;
          case 'last7days':
            transformedData = fillMissingLastNDaysGeneric(response.data, 7).map((item) => ({
              label: item.label,
              maintenanceAlert: (item as any).maintenanceAlert || 0,
              transactionAlert: (item as any).transactionAlert || 0,
              energieConsumptionAlert: (item as any).energieConsumptionAlert || 0,
              performanceAlert: (item as any).performanceAlert || 0,
            }));
            break;
          case 'last24hours':
            transformedData = fillMissingLast24HrsGeneric(response.data).map((item) => ({
              label: item.label,
              maintenanceAlert: (item as any).maintenanceAlert || 0,
              transactionAlert: (item as any).transactionAlert || 0,
              energieConsumptionAlert: (item as any).energieConsumptionAlert || 0,
              performanceAlert: (item as any).performanceAlert || 0,
            }));
            break;
          default:
            transformedData = [];
        }
        setBarGraphData(transformedData);
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Function to handle content type
   * @param key
   */
  const HandleContentType = (key: string) => {
    setDataToggle((previous) => previous.map((tab) => ({ ...tab, isActive: tab.key === key })));
  };

  /**
   * CustomTooltip displays a tooltip with freeSessionCount and paidSessionCount booking hours.
   * @param props - TooltipProps from recharts containing active state and payload.
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="connector-status-custom-tooltip">
          <p
            className="label"
            style={{ color: data?.payload?.color }}>{`${t(data.name)} : ${data.value}`}</p>
        </div>
      );
    }
    return null;
  }

  useEffect(() => {
    fetchToggleData('last24hours');
  }, []);

  return (
    <div className="alerts-chart-wrap">
      <p className="alerts-chart-title">{t('alerts')}</p>
      <div className="alerts-chart-menus">
        <div className="alerts-chart-toggle">
          <CustomToggleButton
            labels={chartTimePeriod.map((duration) => duration.label)}
            onToggle={(index, label) => {
              fetchToggleData(
                chartTimePeriod.find((duration) => duration.label === label)?.value ||
                  'last12months',
              );
              setIsLoading(true);
            }}
            defaultActiveIndex={0}
            type="chart"
          />
          <div className="alerts-data-toggle">
            <ToggleWithIcons
              leftMenu={dataToggle[0]}
              rightMenu={dataToggle[1]}
              handleClickToggle={HandleContentType}
            />
          </div>
        </div>
        <div className="alerts-charts-container">
          {dataToggle[0].isActive ? (
            <div>
              {isLoading ? (
                <div className="loader">
                  <BatteryLoader />
                </div>
              ) : (
                <div className="pie-chart-wrap">
                  <div className="pie-chart-container">
                    <PieChart
                      width={250}
                      height={250}
                      margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                      <Pie
                        data={[
                          {
                            name: 'maintenanceAlert',
                            value: barGraphData.reduce(
                              (sum, d) => sum + (d.maintenanceAlert || 0),
                              0,
                            ),
                            color: '#4ABDFB',
                          },
                          {
                            name: 'transactionAlert',
                            value: barGraphData.reduce(
                              (sum, d) => sum + (d.transactionAlert || 0),
                              0,
                            ),
                            color: '#FB497C',
                          },
                          {
                            name: 'energieConsumptionAlert',
                            value: barGraphData.reduce(
                              (sum, d) => sum + (d.transactionAlert || 0),
                              0,
                            ),
                            color: '#FF7714',
                          },
                          {
                            name: 'performanceAlert',
                            value: barGraphData.reduce(
                              (sum, d) => sum + (d.transactionAlert || 0),
                              0,
                            ),
                            color: '#41DCE5',
                          },
                        ]}
                        cx={125}
                        cy={125}
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        isAnimationActive={false} // Better for touch devices
                      >
                        {[
                          { name: 'maintenanceAlert', color: '#4ABDFB' },
                          { name: 'transactionAlert', color: '#FB497C' },
                          { name: 'energieConsumptionAlert', color: '#FF7714' },
                          { name: 'performanceAlert', color: '#41DCE5' },
                        ].map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={<CustomTooltip />}
                        wrapperStyle={{ zIndex: 1000 }} // Ensure tooltip appears above other elements
                      />
                    </PieChart>
                    <div className="total-sessions-wrap">
                      <p>{t('totalSessions')}</p>
                      <span>
                        {barGraphData.reduce(
                          (sum, d) =>
                            sum +
                            (d.maintenanceAlert || 0) +
                            (d.transactionAlert || 0) +
                            (d.energieConsumptionAlert || 0) +
                            (d.performanceAlert || 0),
                          0,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="pie-chart-heading">
                    <div className="pie-chart-label">
                      {[
                        {
                          name: t('maintenanceAlert'),
                          value: barGraphData.reduce(
                            (sum, d) => sum + (d.maintenanceAlert || 0),
                            0,
                          ),
                          color: '#4ABDFB',
                        },
                        {
                          name: t('transactionAlert'),
                          value: barGraphData.reduce(
                            (sum, d) => sum + (d.transactionAlert || 0),
                            0,
                          ),
                          color: '#FB497C',
                        },
                        {
                          name: t('energyConsumption'),
                          value: barGraphData.reduce(
                            (sum, d) => sum + (d.energieConsumptionAlert || 0),
                            0,
                          ),
                          color: '#FF7714',
                        },
                        {
                          name: t('performanceAlert'),
                          value: barGraphData.reduce(
                            (sum, d) => sum + (d.performanceAlert || 0),
                            0,
                          ),
                          color: '#41DCE5',
                        },
                      ].map((eachData) => (
                        <div key={eachData.name} className="each-label">
                          <div
                            className="each-label-color"
                            style={{ '--label-color': eachData.color } as React.CSSProperties}
                          />
                          <p>{eachData.name}</p>
                          <div className="each-label-value">
                            <span>{eachData.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bar-chart-wrap">
              {isLoading ? (
                <div className="bar-chart-loader">
                  <BatteryLoader />
                </div>
              ) : (
                <div className="bar-chart-container" ref={barChartContainerReference}>
                  <p className="bar-chart-title">{t('frequency')}</p>
                  <BarChart
                    width={500}
                    height={250}
                    data={barGraphData}
                    margin={{
                      top: 50,
                      right: isRTL ? 10 : 0,
                      left: isRTL ? 0 : -10,
                      bottom: 5,
                    }}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0} />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12 }}
                      orientation={isRTL ? 'right' : 'left'}
                    />
                    <Bar dataKey="maintenanceAlert" fill="#4ABDFB" />
                    <Bar dataKey="transactionAlert" fill="#FB497C" />
                    <Bar dataKey="energieConsumptionAlert" fill="#FF7714" />
                    <Bar dataKey="performanceAlert" fill="#41DCE5" />
                  </BarChart>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AlertsChart;
